import { Link } from "react-router";
import { useState, useEffect } from "react";
import { motion } from "motion/react";
import {
  Wind, Flame, Snowflake, Zap, FlaskConical,
  ArrowRight, CheckCircle, MapPin, Map,
  Phone, Award, Users, Clock,
  Gauge, Settings, Headphones, FileSearch, Star
} from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import { servicesService, type Service, parseFeatures } from "../../lib/services/servicesService";
import { productsService, type Product } from "../../lib/services/productsService";
import { projectsService, type Project } from "../../lib/services/projectsService";
import { ensureSession } from "../../lib/appwrite";
import * as Icons from "lucide-react";
import type { Models } from "appwrite";
import heroImage from "../../assets/heroimage.webp";
import client1 from "../../assets/Clients/1.webp";
import client2 from "../../assets/Clients/2.webp";
import client3 from "../../assets/Clients/3.webp";
import client4 from "../../assets/Clients/4.webp";
import client6 from "../../assets/Clients/6.webp";
import client7 from "../../assets/Clients/7.webp";
import client8 from "../../assets/Clients/8.webp";
import client9 from "../../assets/Clients/9.webp";
import client10 from "../../assets/Clients/10.webp";
import client11 from "../../assets/Clients/11.webp";
import client12 from "../../assets/Clients/12.webp";
import client13 from "../../assets/Clients/13.webp";
import client14 from "../../assets/Clients/14.webp";
import clientBHR from "../../assets/Clients/BHR.webp";

const clientLogos = [
  client1, client2, client3, client4, client6, client7, client8,
  client9, client10, client11, client12, client13, client14, clientBHR
];

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } },
};



const stats = [
  { icon: Award, value: "12+", label: "Years in Japanese OEM Industry" },
  { icon: Users, value: "100+", label: "Projects Delivered" },
  { icon: Clock, value: "4", label: "Core Team Members" },
  { icon: Gauge, value: "3", label: "States Served" },
];

const testimonials = [
  { name: "Manufacturing Client", role: "Industrial Project, Rajasthan", text: "Solace Smart Solutions delivered our complete HVAC system on time with exceptional precision. Their Japanese methodology approach to quality and safety is evident in every aspect.", rating: 5 },
  { name: "Hotel Partner", role: "Hospitality Sector, Gujarat", text: "The heat pump water heating system installed by Solace has drastically reduced our energy bills. Their team was professional from survey to final commissioning.", rating: 5 },
  { name: "Cold Chain Operator", role: "Food Processing, Uttar Pradesh", text: "The cold room fabricated by Solace has been maintaining perfect temperatures. Their understanding of refrigeration requirements gives us complete peace of mind.", rating: 5 },
  { name: "Pharma Manufacturer", role: "Cleanroom Facility, Baddi", text: "The industrial rooftop HVAC units they provided perfectly maintain the positive pressure across our cleanrooms. Excellent post-installation service and trained technicians.", rating: 5 },
  { name: "Retail Mall Director", role: "Commercial Spaces, Jaipur", text: "Upgrading to their VRV system was the best decision for our mall. The multi-zone control works flawlessly, and energy efficiency has exceeded our expectations.", rating: 5 },
];

type ProductDoc = Product & Models.Document;
type ProjectDoc = Project & Models.Document;

// Static fallback items shown when Appwrite returns 0 products
const STATIC_PRODUCTS = [
  { icon: Wind, category: "HVAC", name: "VRV/VRF Split System", spec: "4 HP – 100 HP | Multi-zone", color: "bg-blue-50 text-blue-600", img: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800" },
  { icon: Wind, category: "HVAC", name: "Cassette & Ceiling AC", spec: "360° airflow | Low noise", color: "bg-blue-50 text-blue-600", img: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800" },
  { icon: Flame, category: "Heat Pump", name: "Air-Source Water Heater", spec: "Up to 75% energy savings", color: "bg-orange-50 text-orange-600", img: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800" },
  { icon: Snowflake, category: "Refrigeration", name: "Walk-In Cold Room", spec: "0°C to +10°C | Custom build", color: "bg-cyan-50 text-cyan-600", img: "https://images.unsplash.com/photo-1584568694244-14fbdf83bd30?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800" },
  { icon: Snowflake, category: "Refrigeration", name: "Industrial Fridge Plant", spec: "High COP | Centralised", color: "bg-cyan-50 text-cyan-600", img: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800" },
  { icon: Zap, category: "Electrical", name: "MCC / PCC Control Panel", spec: "Custom fabrication | IP55", color: "bg-amber-50 text-amber-600", img: "https://images.unsplash.com/photo-1555664424-778a1e5e1b48?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800" },
  { icon: Zap, category: "Electrical", name: "VFD Drive Panel", spec: "40% energy saving | Soft-start", color: "bg-amber-50 text-amber-600", img: "https://images.unsplash.com/photo-1518770660439-4636190af475?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800" },
  { icon: FlaskConical, category: "Refrigerant", name: "ISO Tank Auto System", spec: "India's 1st | Fully Automatic", color: "bg-purple-50 text-purple-600", img: "https://images.unsplash.com/photo-1582719508461-905c673771fd?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800" },
];

import { SEO } from "./SEO";
// ... imports ...

export function HomePage() {
  const [homeProducts, setHomeProducts] = useState<ProductDoc[] | null>(null);
  const [homeServices, setHomeServices] = useState<Service[]>([]);
  const [homeProjects, setHomeProjects] = useState<ProjectDoc[] | null>(null);
  const [loadingServices, setLoadingServices] = useState(true);

  useEffect(() => {
    const initAndFetch = async () => {
      // Ensure guest session is established on first load to prevent race conditions
      await ensureSession();

      // Fetch Products independently
      productsService.list()
        .then((all) => {
          const active = all.filter((p) => p.status !== "Inactive");
          setHomeProducts(active.slice(0, 8));
        })
        .catch(() => setHomeProducts([]));

      // Fetch Services independently (loads faster)
      servicesService.list()
        .then((all) => {
          const active = all.filter((s) => s.status !== "Inactive");
          // Sort by sortOrder (matching ServicesPage) and take top 3
          setHomeServices(active.sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder)).slice(0, 3));
        })
        .catch(console.error)
        .finally(() => setLoadingServices(false));

      // Fetch Projects independently
      projectsService.list()
        .then((all) => {
          setHomeProjects(all.slice(0, 6));
        })
        .catch(() => setHomeProjects([]));
    };

    initAndFetch();
  }, []);

  return (
    <div>
      <SEO
        title="Home | Solace SMART Solutions"
        description="India's leading provider of HVAC, Refrigeration, Heat Pump, and Electrical solutions using Japanese methodologies for optimum cost and precision."
      />

      {/* Hero Section */}
      <section className="relative min-h-0 md:min-h-[92vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <ImageWithFallback
            src={heroImage}
            alt="Solace SMART Solutions HVAC engineer at work"
            className="w-full h-full object-cover object-[65%_center] md:object-[75%_center] lg:object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b md:bg-gradient-to-r from-[#0f1b30]/95 via-[#0f1b30]/60 md:via-[#0f1b30]/85 to-transparent md:to-[#0f1b30]/40" />
        </div>
        <div className="relative max-w-screen-2xl mx-auto px-5 md:px-6 py-8 md:py-20 w-full mt-2 md:mt-0">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-2xl text-left">
            <motion.div variants={fadeUp} className="inline-flex items-center gap-1.5 md:gap-2 bg-[#E9A319]/20 text-[#E9A319] px-3 md:px-4 py-1 md:py-1.5 rounded-full mb-3 md:mb-6 text-[11px] md:text-sm backdrop-blur-sm border border-[#E9A319]/30 tracking-wide">
              <Wind className="w-3.5 h-3.5 md:w-4 md:h-4" /> HVAC · Refrigeration · Electrical
            </motion.div>
            <motion.h1 variants={fadeUp} className="text-[2rem] leading-[1.1] sm:text-5xl md:text-5xl lg:text-6xl text-white mb-3 md:mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
              Need Solid &amp; Optimum Cost <span className="text-[#E9A319] md:inline mt-1 md:mt-0">Solutions?</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-sm sm:text-lg text-gray-300 mb-6 max-w-xl mx-0" style={{ lineHeight: 1.6 }}>
              Professionals to serve India with Japanese methodologies. We provide complete HVAC, Refrigeration, Heat Pump &amp; Electrical solutions — from survey and design to installation, commissioning &amp; maintenance.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 items-start">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-[#E9A319] text-[#1a2d50] px-8 py-4 rounded-full hover:bg-[#d4920f] transition-all shadow-xl shadow-[#E9A319]/30"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700 }}
              >
                Get a Free Quote <ArrowRight className="w-4 h-4" />
              </Link>
              <a
                href="tel:+918800878095"
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-full hover:bg-white/20 transition-all border border-white/20"
                style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600 }}
              >
                <Phone className="w-4 h-4" /> +91 88008-78095
              </a>
            </motion.div>
            <motion.div variants={fadeUp} className="flex flex-wrap items-center justify-start gap-x-6 gap-y-3 mt-10">
              {["Japanese Methodologies", "SQDMC Culture", "Turnkey Solutions"].map((item) => (
                <span key={item} className="flex items-center gap-2 text-gray-300 text-sm">
                  <CheckCircle className="w-4 h-4 text-[#E9A319]" /> {item}
                </span>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-[#111e36] py-8 border-b border-white/5 relative z-20 shadow-lg">
        <div className="max-w-screen-2xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-10">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-4 group"
              >
                <div className="w-[52px] h-[52px] bg-white/5 hover:bg-white/10 transition-colors rounded-[14px] flex items-center justify-center shrink-0 border border-white/10 shadow-inner">
                  <stat.icon className="w-[22px] h-[22px] text-[#E9A319]" strokeWidth={1.8} />
                </div>
                <div className="space-y-0.5">
                  <p className="text-[26px] text-white tracking-tight leading-none" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>{stat.value}</p>
                  <p className="text-[#a5b4cb] text-[13px] font-medium leading-tight max-w-[150px]">{stat.label}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services / Expertise Segment */}
      <section className="py-24 bg-[#f8f6f1]">
        <div className="max-w-screen-2xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.span variants={fadeUp} className="text-[#E9A319] text-sm tracking-widest uppercase" style={{ fontWeight: 600 }}>Our Expertise</motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl text-[#1a2d50] mt-3 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
              Complete Engineering Solutions
            </motion.h2>
            <motion.p variants={fadeUp} className="text-muted-foreground max-w-2xl mx-auto" style={{ lineHeight: 1.8 }}>
              From HVAC design to India's first refrigerant ISO tank system — we deliver every project with Japanese precision, from survey to after-sales support.
            </motion.p>
          </motion.div>

          <motion.div
            key={loadingServices ? "loading" : "content"}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {loadingServices ? (
              // Loading Skeleton
              Array(3).fill(null).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden border border-[#1a2d50]/5 shadow-sm animate-pulse h-[400px]">
                  <div className="h-48 bg-gray-200" />
                  <div className="p-5">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-4" />
                    <div className="h-3 bg-gray-200 rounded w-full mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-5/6 mb-4" />
                    <div className="space-y-2">
                      <div className="h-3 bg-gray-200 rounded w-1/2" />
                      <div className="h-3 bg-gray-200 rounded w-2/3" />
                    </div>
                  </div>
                </div>
              ))
            ) : homeServices.map((service) => {
              const Icon = (Icons as any)[service.icon] || Icons.Settings;
              const feats = parseFeatures(service.features);
              return (
                <motion.div
                  key={service.$id}
                  variants={fadeUp}
                  className="bg-white rounded-2xl overflow-hidden border border-[#1a2d50]/5 hover:shadow-xl transition-all duration-300 group"
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.img}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => { (e.target as HTMLImageElement).src = "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=600"; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a2d50]/80 via-transparent to-transparent" />
                    <div className="absolute top-4 left-4 w-10 h-10 rounded-xl flex items-center justify-center backdrop-blur-sm border" style={{ backgroundColor: `${service.colorAccent || '#1a2d50'}20`, borderColor: `${service.colorAccent || '#1a2d50'}30`, color: service.colorAccent || '#1a2d50' }}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <h3 className="absolute bottom-4 left-4 right-4 text-white text-base leading-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>
                      {service.title}
                    </h3>
                  </div>
                  {/* Body */}
                  <div className="p-5">
                    <p className="text-muted-foreground text-sm mb-4" style={{ lineHeight: 1.75 }}>
                      {service.desc.split('.')[0]}.
                    </p>
                    <div className="space-y-1.5 mb-5">
                      {feats.slice(0, 3).map((f, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <CheckCircle className="w-3.5 h-3.5 text-[#E9A319] shrink-0 mt-0.5" />
                          <span className="text-xs text-[#1a2d50]/70" style={{ lineHeight: 1.5 }}>{f}</span>
                        </div>
                      ))}
                    </div>
                    <Link
                      to={`/services/${service.slug}`}
                      className="inline-flex items-center gap-2 text-[#E9A319] text-sm hover:gap-3 transition-all"
                      style={{ fontWeight: 600 }}
                    >
                      Learn More <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </motion.div>
              )
            })}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }} className="text-right mt-12">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 bg-[#1a2d50] text-white px-8 py-3.5 rounded-full hover:bg-[#243d6a] transition-all"
              style={{ fontWeight: 600 }}
            >
              View All Services <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Products Teaser Section */}
      <section className="py-20 bg-white">
        <div className="max-w-screen-2xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <motion.span variants={fadeUp} className="text-[#E9A319] text-sm tracking-widest uppercase" style={{ fontWeight: 600 }}>Products We Supply</motion.span>
              <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl text-[#1a2d50] mt-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
                Equipment That Powers Our Solutions
              </motion.h2>
              <motion.p variants={fadeUp} className="text-muted-foreground mt-3 max-w-xl" style={{ lineHeight: 1.8 }}>
                Every product we supply is backed by our full turnkey service — survey, design, installation, and after-sales support included.
              </motion.p>
            </div>
            <motion.div variants={fadeUp} className="shrink-0">
              <Link
                to="/products"
                className="inline-flex items-center gap-2 border-2 border-[#1a2d50] text-[#1a2d50] px-6 py-3 rounded-full hover:bg-[#1a2d50] hover:text-white transition-all"
                style={{ fontWeight: 600 }}
              >
                View All Products <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>

          {/* Dynamic products from Appwrite, fallback to static list */}
          <motion.div
            key={homeProducts === null ? "loading" : "content"}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5"
          >
            {(homeProducts === null
              ? STATIC_PRODUCTS  // still loading — show static
              : homeProducts.length > 0
                ? homeProducts.map((p) => ({
                  icon: ({ className }: { className?: string }) => <Wind className={className} />, // generic icon
                  category: p.category,
                  name: p.name,
                  spec: p.features ? p.features.split(",")[0].trim() : p.brand,
                  color: "bg-blue-50 text-blue-600",
                  img: p.imageUrl || "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800",
                }))
                : STATIC_PRODUCTS  // 0 results — show static fallback
            ).map((item, idx) => (
              <motion.div key={idx} variants={fadeUp}>
                <Link
                  to="/products"
                  className="block bg-white rounded-2xl overflow-hidden border border-[#1a2d50]/5 hover:shadow-xl hover:border-[#E9A319]/20 transition-all duration-300 group"
                >
                  {/* Image */}
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={item.img}
                      alt={item.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=400";
                      }}
                    />
                    <div className={`absolute top-3 left-3 ${item.color} px-2.5 py-1 rounded-full text-xs flex items-center gap-1.5 backdrop-blur-sm bg-white/80`} style={{ fontWeight: 600 }}>
                      <item.icon className="w-3 h-3" />
                      {item.category}
                    </div>
                  </div>
                  {/* Body */}
                  <div className="p-5">
                    <h3 className="text-[#1a2d50] mb-1 text-sm leading-snug" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>{item.name}</h3>
                    <p className="text-[#E9A319] text-xs mb-3" style={{ fontWeight: 600 }}>{item.spec}</p>
                    <div className="flex items-center gap-1 text-xs text-[#1a2d50]/50 group-hover:text-[#E9A319] transition-colors" style={{ fontWeight: 600 }}>
                      View Product <ArrowRight className="w-3 h-3" />
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="py-24 bg-[#f8f6f1] overflow-hidden">
        <div className="max-w-screen-2xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Images grid */}
            <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <img
                    src="https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=600"
                    alt="HVAC installation"
                    className="w-full h-48 object-cover rounded-2xl shadow-lg"
                  />
                  <img
                    src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=600"
                    alt="Heat pump system"
                    className="w-full h-32 object-cover rounded-2xl shadow-lg"
                  />
                </div>
                <div className="space-y-4 pt-8">
                  <img
                    src="https://images.unsplash.com/photo-1584568694244-14fbdf83bd30?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=600"
                    alt="Cold room refrigeration"
                    className="w-full h-32 object-cover rounded-2xl shadow-lg"
                  />
                  <img
                    src="https://images.unsplash.com/photo-1555664424-778a1e5e1b48?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=600"
                    alt="Electrical panel"
                    className="w-full h-48 object-cover rounded-2xl shadow-lg"
                  />
                </div>
              </div>
            </motion.div>

            {/* Text */}
            <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
              <span className="text-[#E9A319] text-sm tracking-widest uppercase" style={{ fontWeight: 600 }}>About Us</span>
              <h2 className="text-3xl md:text-4xl text-[#1a2d50] mt-3 mb-5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
                Turnkey Engineering Excellence, Delivered with Japanese Precision
              </h2>
              <p className="text-muted-foreground mb-6" style={{ lineHeight: 1.8 }}>
                Founded by Nitin Kr. Singh — B.Tech Mechanical Engineer with 12+ years in Japanese OEM Manufacturing — Solace SMART Solutions was built to give clients the very best in HVAC consultation, project management, and precision execution.
              </p>
              <div className="grid sm:grid-cols-2 gap-3 mb-8">
                {[
                  "Design & Consultation",
                  "Sales & Supply",
                  "Installation & Commissioning",
                  "After-Sales AMC",
                  "SQDMC Japanese Culture",
                  "3 States: RJ · UP · GJ",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2.5 bg-white rounded-xl px-4 py-3 border border-[#1a2d50]/5">
                    <CheckCircle className="w-4 h-4 text-[#E9A319] shrink-0" />
                    <span className="text-[#1a2d50] text-sm" style={{ fontWeight: 500 }}>{item}</span>
                  </div>
                ))}
              </div>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 bg-[#E9A319] text-white px-8 py-3.5 rounded-full hover:bg-[#d4920f] transition-all shadow-lg shadow-[#E9A319]/20"
                style={{ fontWeight: 600 }}
              >
                More About Us <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How It Works / Our Process */}
      <section className="py-24 bg-gradient-to-br from-[#1a2d50] via-[#1f3561] to-[#243d6a] text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-[#E9A319] rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-300 rounded-full blur-3xl" />
        </div>
        <div className="max-w-screen-2xl mx-auto px-6 relative z-10">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.span variants={fadeUp} className="text-[#E9A319] text-sm tracking-widest uppercase" style={{ fontWeight: 600 }}>Our Process</motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl mt-3 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
              How We Deliver Every Project
            </motion.h2>
            <motion.p variants={fadeUp} className="text-blue-100 max-w-2xl mx-auto" style={{ lineHeight: 1.8 }}>
              Our proven process ensures every project is delivered with precision, on time, and within budget — following Japanese engineering standards.
            </motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: Phone, title: "1. Survey", desc: "We begin with a detailed site survey to understand your requirements, space constraints, and operational needs." },
              { icon: FileSearch, title: "2. Design & Consultation", desc: "Our engineers create optimum cost solutions with detailed designs, precise calculations, and transparent pricing." },
              { icon: Settings, title: "3. Installation", desc: "Our certified technicians execute the installation with precision, following SQDMC standards for maximum quality." },
              { icon: Headphones, title: "4. Commissioning & AMC", desc: "Full commissioning, testing, handover training, and optional Annual Maintenance Contract for ongoing peace of mind." },
            ].map((item) => (
              <motion.div key={item.title} variants={fadeUp} className="text-center">
                <div className="w-16 h-16 bg-[#E9A319]/15 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-5 border border-[#E9A319]/20">
                  <item.icon className="w-8 h-8 text-[#E9A319]" />
                </div>
                <h3 className="text-lg mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>{item.title}</h3>
                <p className="text-blue-200 text-sm" style={{ lineHeight: 1.7 }}>{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-24 bg-white">
        <div className="max-w-screen-2xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
            <div>
              <motion.span variants={fadeUp} className="text-[#E9A319] text-sm tracking-widest uppercase" style={{ fontWeight: 600 }}>Recent Projects</motion.span>
              <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl text-[#1a2d50] mt-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
                Projects That Speak for Themselves
              </motion.h2>
              <motion.p variants={fadeUp} className="text-muted-foreground mt-3 max-w-xl" style={{ lineHeight: 1.8 }}>
                Every project we complete is a commitment — to quality, precision, and client satisfaction. Here's a glimpse of our recent work.
              </motion.p>
            </div>
            <motion.div variants={fadeUp} className="shrink-0">
              <Link to="/projects" className="inline-flex items-center gap-2 bg-[#E9A319] text-white px-6 py-3 rounded-full hover:bg-[#d4920f] transition-all shadow-md shadow-[#E9A319]/20" style={{ fontWeight: 600 }}>
                View All Projects <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>

          <motion.div
            key={homeProjects === null ? "loading" : "content"}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {(homeProjects === null
              ? [
                {
                  imageUrl: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800",
                  category: "HVAC",
                  title: "Central HVAC System — Shopping Mall",
                  location: "Jaipur, Rajasthan",
                  scope: "Survey · Design · Installation · AMC",
                  outcome: "42-TR VRF system serving 3 floors, 18°C maintained year-round",
                  status: "Completed",
                },
                {
                  imageUrl: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800",
                  category: "Heat Pump",
                  title: "Heat Pump Water Heating — Hospital",
                  location: "Greater Noida, UP",
                  scope: "Design · Supply · Commissioning",
                  outcome: "200 LPD system · 70% reduction in hot water energy costs",
                  status: "Completed",
                },
                {
                  imageUrl: "https://images.unsplash.com/photo-1584568694244-14fbdf83bd30?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=800",
                  category: "Cold Room",
                  title: "Walk-In Cold Room — Food Processing Unit",
                  location: "Halol, Gujarat",
                  scope: "Fabrication · Refrigeration · PLC Controls",
                  outcome: "−18°C to +4°C cold chain · 500 MT storage capacity",
                  status: "Completed",
                },
              ]
              : homeProjects.length > 0 ? homeProjects : []
            ).map((project) => (
              <motion.div key={project.title} variants={fadeUp} className="bg-[#f8f6f1] rounded-2xl overflow-hidden border border-[#1a2d50]/5 hover:shadow-xl transition-all duration-300 group flex flex-col">
                {/* Image */}
                <div className="relative h-48 overflow-hidden shrink-0">
                  <img
                    src={project.imageUrl || "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=400"}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => { (e.target as HTMLImageElement).src = "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&q=80&w=400"; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1a2d50]/60 via-transparent to-transparent pointer-events-none" />
                  {/* Category badge */}
                  <span className={`absolute top-3 left-3 bg-[#1a2d50] text-white text-xs px-2.5 py-1 rounded-full`} style={{ fontWeight: 600 }}>
                    {project.category}
                  </span>
                  {/* Status badge */}
                  <span className={`absolute top-3 right-3 text-xs px-2.5 py-1 rounded-full ${project.status === "Ongoing" ? "bg-[#E9A319] text-white" : "bg-white/90 text-[#1a2d50]"}`} style={{ fontWeight: 600 }}>
                    {project.status}
                  </span>
                </div>
                {/* Body */}
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-2">
                    <Users className="w-3 h-3" />
                    {project.location}
                  </div>
                  <h3 className="text-[#1a2d50] mb-2 text-sm leading-snug" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>
                    {project.title}
                  </h3>
                  <p className="text-xs text-[#E9A319] mb-3" style={{ fontWeight: 600 }}>{project.scope}</p>
                  <div className="flex items-start gap-2 bg-white rounded-xl p-3 border border-[#1a2d50]/5 mt-auto">
                    <CheckCircle className="w-3.5 h-3.5 text-[#E9A319] shrink-0 mt-0.5" />
                    <p className="text-xs text-[#1a2d50]/70" style={{ lineHeight: 1.5 }}>{project.outcome}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Clients Section */}
      <section className="py-20 bg-white overflow-hidden border-b border-[#1a2d50]/5">
        <style>
          {`
            @keyframes marquee-clients {
              0% { transform: translateX(0); }
              100% { transform: translateX(calc(-50% - 1.5rem)); }
            }
            .animate-marquee-clients {
              animation: marquee-clients 35s linear infinite;
              display: flex;
              width: max-content;
            }
          `}
        </style>
        <div className="max-w-screen-2xl mx-auto px-6 mb-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center">
            <motion.span variants={fadeUp} className="text-[#E9A319] text-sm tracking-widest uppercase" style={{ fontWeight: 600 }}>Trusted By</motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl text-[#1a2d50] mt-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
              Our Esteemed Clients
            </motion.h2>
          </motion.div>
        </div>

        {/* Clients Marquee Wrapper */}
        <div className="w-full relative px-6 max-w-full mx-auto">
          {/* Gradient Edges */}
          <div className="absolute left-0 top-0 bottom-0 w-16 md:w-48 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-16 md:w-48 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none"></div>

          <div className="animate-marquee-clients gap-12 items-center">
            {[...clientLogos, ...clientLogos].map((logo, index) => (
              <div key={`client-${index}`} className="w-32 sm:w-48 h-20 shrink-0 flex items-center justify-center grayscale hover:grayscale-0 transition-all duration-300 opacity-60 hover:opacity-100">
                <img src={logo} alt={`Client logo ${index}`} className="max-w-full max-h-full object-contain" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-[#f8f6f1] overflow-hidden">
        <style>
          {`
            @keyframes marquee {
              0% { transform: translateX(0); }
              100% { transform: translateX(calc(-50% - 1rem)); }
            }
            .animate-marquee {
              animation: marquee 40s linear infinite;
              display: flex;
              width: max-content;
            }
            .animate-marquee:hover {
              animation-play-state: paused;
            }
          `}
        </style>
        <div className="max-w-screen-2xl mx-auto px-6 mb-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center">
            <motion.span variants={fadeUp} className="text-[#E9A319] text-sm tracking-widest uppercase" style={{ fontWeight: 600 }}>Testimonials</motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl text-[#1a2d50] mt-3 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
              What Our Clients Say
            </motion.h2>
            <motion.p variants={fadeUp} className="text-muted-foreground max-w-2xl mx-auto" style={{ lineHeight: 1.8 }}>
              Thousands of professionals and businesses are already enjoying our service. Hear what some of them are saying!
            </motion.p>
          </motion.div>
        </div>

        {/* Marquee Wrapper */}
        <div className="w-full relative px-6 max-w-full mx-auto">
          {/* Gradient Edges */}
          <div className="absolute left-0 top-0 bottom-0 w-16 md:w-48 bg-gradient-to-r from-[#f8f6f1] to-transparent z-10 pointer-events-none"></div>
          <div className="absolute right-0 top-0 bottom-0 w-16 md:w-48 bg-gradient-to-l from-[#f8f6f1] to-transparent z-10 pointer-events-none"></div>

          <div className="animate-marquee gap-8">
            {[...testimonials, ...testimonials].map((t, index) => (
              <div key={`${t.name}-${index}`} className="w-[320px] sm:w-[380px] shrink-0 bg-white rounded-2xl p-6 shadow-sm border border-[#1a2d50]/5 flex flex-col h-full cursor-grab active:cursor-grabbing hover:shadow-lg transition-shadow">
                <div className="flex gap-1 mb-4 flex-shrink-0">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-[#E9A319] text-[#E9A319]" />
                  ))}
                </div>
                <p className="text-[#1a2d50]/80 mb-6 text-sm flex-grow" style={{ lineHeight: 1.7 }}>"{t.text}"</p>
                <div className="flex items-center gap-3 mt-auto flex-shrink-0 pt-4 border-t border-gray-100">
                  <div className="w-10 h-10 bg-[#1a2d50]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-[#1a2d50]" style={{ fontWeight: 700 }}>{t.name.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="text-[#1a2d50] text-sm" style={{ fontWeight: 600 }}>{t.name}</p>
                    <p className="text-muted-foreground text-xs">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Office Locations */}
      <section className="py-20 bg-gradient-to-br from-[#1a2d50] via-[#1f3561] to-[#243d6a] relative overflow-hidden">
        {/* Background blobs */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-10 left-10 w-64 h-64 bg-[#E9A319] rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-blue-300 rounded-full blur-3xl" />
        </div>
        <div className="max-w-screen-2xl mx-auto px-6 relative z-10">
          {/* Header */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.span variants={fadeUp} className="text-[#E9A319] text-sm tracking-widest uppercase" style={{ fontWeight: 600 }}>Pan-India Presence</motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl text-white mt-3 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
              Our Office Locations
            </motion.h2>
            <motion.p variants={fadeUp} className="text-blue-200 max-w-xl mx-auto" style={{ lineHeight: 1.8 }}>
              Serving clients across Rajasthan, Uttar Pradesh & Gujarat — with local teams ready to survey, design and deliver.
            </motion.p>
          </motion.div>

          {/* Cards */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-6">
            {[
              {
                state: "Rajasthan",
                city: "Jaipur",
                address: "B12, Pratap Plaza, Pratap Nagar, Jaipur",
                phone: "+91 88008 78095",
                mapUrl: "https://maps.google.com/?q=Pratap+Plaza+Pratap+Nagar+Jaipur",
              },
              {
                state: "Uttar Pradesh",
                city: "Greater Noida",
                address: "B-488, XU III, Greater Noida",
                phone: "+91 88755 55358",
                mapUrl: "https://maps.google.com/?q=XU+III+Greater+Noida",
              },
              {
                state: "Gujarat",
                city: "Halol",
                address: "14, Sun Enclave, Halol, Gujarat",
                phone: "+91 88008 78095",
                mapUrl: "https://maps.google.com/?q=Sun+Enclave+Halol+Gujarat",
              },
            ].map((office, i) => (
              <motion.div
                key={office.state}
                variants={fadeUp}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-5 border border-white/10 hover:bg-white/10 hover:border-[#E9A319]/30 transition-all duration-300 group flex flex-col h-full"
              >
                {/* Top row */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-white text-lg" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>{office.state}</h3>
                    <p className="text-blue-300 text-xs">{office.city}</p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                    <Map className="w-5 h-5 text-[#E9A319]" />
                  </div>
                </div>

                {/* Address */}
                <div className="flex items-start gap-2.5 mb-4 flex-grow">
                  <MapPin className="w-4 h-4 text-[#E9A319] shrink-0 mt-0.5" />
                  <p className="text-blue-200 text-sm" style={{ lineHeight: 1.6 }}>{office.address}</p>
                </div>

                {/* Phone & Link row */}
                <div className="flex items-center justify-between mt-auto pt-4 border-t border-white/10">
                  <div className="flex items-center gap-2">
                    <Phone className="w-3.5 h-3.5 text-[#E9A319] shrink-0" />
                    <a href={`tel:${office.phone.replace(/\s/g, '')}`} className="text-white text-sm hover:text-[#E9A319] transition-colors" style={{ fontWeight: 600 }}>
                      {office.phone}
                    </a>
                  </div>
                  {/* Map link */}
                  <a
                    href={office.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-[0.65rem] uppercase tracking-wider text-[#E9A319] hover:text-white transition-all"
                    style={{ fontWeight: 700 }}
                  >
                    View Map <ArrowRight className="w-3 h-3" />
                  </a>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
